import { call, put, takeLatest, select, all } from 'redux-saga/effects';
import { rsdFormsActions } from './slice';
import { PayloadAction } from '@reduxjs/toolkit';
import { Moment, MomentType, FormField } from './slice';
import { api } from './api';
import { RootState } from './store';

// Selectors
const selectFormFieldsCache = (state: RootState) =>
  state.rsdForms.formFieldsCache;

// Worker Sagas
function* loadFormFieldsSaga(action: PayloadAction<string>) {
  try {
    const momentTypeId = action.payload;

    // Check if we already have this form fields cached
    const formFieldsCache: Record<string, FormField[]> = yield select(
      selectFormFieldsCache
    );

    // If we have cached data, use it instead of making an API call
    if (formFieldsCache[momentTypeId]) {
      console.log(`Using cached form fields for moment type: ${momentTypeId}`);
      // No need to dispatch any action as we're already using the cached data
      return;
    }

    // If not cached, fetch from API
    console.log(`Fetching form fields for moment type: ${momentTypeId}`);
    const formFields: FormField[] = yield call(
      api.fetchFormFields,
      momentTypeId
    );
    yield put(
      rsdFormsActions.formFieldsLoaded({
        momentTypeId,
        fields: formFields
      })
    );
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to load form fields';
    yield put(rsdFormsActions.formFieldsLoadError(errorMessage));
  }
}

function* createMomentSaga(
  action: PayloadAction<Omit<Moment, 'id' | 'createdAt'>>
) {
  console.log('[saga] createMomentSaga triggered with:', action.payload);
  try {
    const newMoment: Moment = yield call(api.createMoment, action.payload);
    yield put(rsdFormsActions.momentCreated(newMoment));
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to create moment';
    yield put(rsdFormsActions.momentCreateError(errorMessage));
  }
}

// Load moment types only
function* loadMomentTypesSaga() {
  try {
    const momentTypes: MomentType[] = yield call(api.fetchMomentTypes);
    yield put(rsdFormsActions.momentTypesLoaded(momentTypes));
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to load moment types';
    yield put(rsdFormsActions.momentTypesLoadError(errorMessage));
  }
}

// Load form configs only
function* loadAllFormConfigsSaga() {
  try {
    const allFormConfigs = yield call(api.fetchAllFormConfigs);
    yield put(rsdFormsActions.allFormConfigsLoaded(allFormConfigs));
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to load form configs';
    yield put(rsdFormsActions.allFormConfigsLoadError(errorMessage));
  }
}

// Watcher Saga
export function* rsdFormsSaga() {
  yield takeLatest(rsdFormsActions.loadMomentTypes.type, loadMomentTypesSaga);
  yield takeLatest(
    rsdFormsActions.loadAllFormConfigs.type,
    loadAllFormConfigsSaga
  );
  yield takeLatest(rsdFormsActions.loadFormFields.type, loadFormFieldsSaga);
  yield takeLatest(rsdFormsActions.createMoment.type, createMomentSaga);
}
